import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, User, Phone, MapPin, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import IntroLogoAnimation from '../vitrine/IntroLogoAnimation';

const LOGO_URL = "/logo.png";

const navLinks = [
  { path: '/', label: 'Accueil' },
  { path: '/nos-matieres', label: 'Nos Matières' },
  { path: '/realisations', label: 'Réalisations' },
  { path: '/savoir-faire', label: 'Savoir-Faire' },
  { path: '/contact', label: 'Contact' },
];

// Palette luxe
const COPPER = '#B8895A';
const INK = '#2B1C12';
const CREAM = '#F5E6D0';

export default function VitrineLayout({ children, heroLightMode = false, autoHideHeader = false }) {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const [hidden, setHidden] = useState(false); // header auto-hide for filter-heavy pages

  // Light hero mode: page declares the hero is on a light/cream marble background
  // → header should use dark INK text. Otherwise (dark hero / other pages) use CREAM text.
  const lightHeader = heroLightMode && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 80);
      if (autoHideHeader) {
        // Hide header as soon as user scrolls past ~120px (top of hero block).
        // Show again ONLY when at the very top (y < 10) — prevents flicker on scroll-up.
        if (y > 120) {
          setHidden(true);
        } else if (y < 10) {
          setHidden(false);
        }
      } else {
        setHidden(false);
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [autoHideHeader]);

  useEffect(() => setIsMenuOpen(false), [location]);

  // Intro lock
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const played = window.sessionStorage.getItem('granipal_intro_played');
    if (played) { setIntroDone(true); return; }
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleIntroComplete = () => {
    document.body.style.overflow = '';
    setIntroDone(true);
  };

  // Colors derived from header state — strong contrast for premium feel
  const navColor      = lightHeader ? 'rgba(43,28,18,0.92)' : 'rgba(245,230,208,0.92)';
  const navActiveCol  = COPPER;
  const accentColor   = lightHeader ? INK : CREAM;
  const accentBorder  = lightHeader ? 'rgba(139,94,47,0.55)' : 'rgba(212,168,120,0.55)';
  // Header is ALWAYS a NEUTRAL translucent glass — no warm/cream tint that could
  // appear yellowish on light sections. Pure white-blur over background.
  // Stronger blur + slight opacity for a truly visible glassmorphism effect.
  const headerStyle = isScrolled
    ? {
        background: 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(36px) saturate(140%)',
        WebkitBackdropFilter: 'blur(36px) saturate(140%)',
        borderBottom: '1px solid rgba(255,255,255,0.14)',
        boxShadow: '0 10px 36px -12px rgba(0,0,0,0.32)',
      }
    : {
        background: 'transparent',
      };

  return (
    <div className="min-h-screen bg-[#100B07]" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <IntroLogoAnimation onComplete={handleIntroComplete} />

      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: introDone ? (hidden ? -120 : 0) : -100,
          opacity: introDone ? 1 : 0,
        }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500`}
        style={headerStyle}
        data-testid="vitrine-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <Link to="/" className="relative z-10" data-testid="header-logo-link">
              <img
                src={LOGO_URL}
                alt="Granipal"
                className="h-[72px] lg:h-[88px] w-auto object-contain transition-[filter] duration-500"
                style={{
                  // Invert/tone the logo subtly so it sits correctly on light marble OR dark bg
                  filter: lightHeader ? 'none' : 'brightness(1.05)',
                }}
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => {
                const active = location.pathname === link.path;
                // Premium text-shadow for relief + readability on any background
                const navShadow = lightHeader
                  ? '0 1px 0 rgba(255,250,240,0.55), 0 0 16px rgba(184,137,90,0.18)'
                  : '0 1px 2px rgba(0,0,0,0.45), 0 0 14px rgba(184,137,90,0.18)';
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="group relative text-[11px] uppercase tracking-[0.3em] font-semibold transition-colors duration-500"
                    style={{ color: active ? navActiveCol : navColor, textShadow: navShadow }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = COPPER; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = navColor; }}
                    data-testid={`nav-${link.path === '/' ? 'home' : link.path.slice(1)}`}
                  >
                    {link.label}
                    <span
                      aria-hidden
                      className="absolute -bottom-1.5 left-0 h-px transition-all duration-500"
                      style={{ width: active ? '100%' : '0%', background: COPPER }}
                    />
                  </Link>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <Link
                to="/login"
                className="group relative flex items-center gap-2 px-6 py-2.5 text-[11px] uppercase tracking-[0.24em] font-semibold transition-all duration-500 overflow-hidden"
                style={{
                  color: accentColor,
                  border: '1px solid rgba(184,137,90,0.70)',
                  borderRadius: '2px',
                  background: lightHeader
                    ? 'linear-gradient(135deg, rgba(255,250,240,0.30) 0%, rgba(184,137,90,0.08) 100%)'
                    : 'linear-gradient(135deg, rgba(184,137,90,0.10) 0%, rgba(245,230,208,0.05) 100%)',
                  backdropFilter: 'blur(14px) saturate(140%)',
                  WebkitBackdropFilter: 'blur(14px) saturate(140%)',
                  boxShadow: lightHeader
                    ? 'inset 0 1px 0 rgba(255,250,240,0.45), 0 4px 16px -4px rgba(139,94,47,0.30)'
                    : 'inset 0 1px 0 rgba(255,250,240,0.12), 0 4px 18px -4px rgba(0,0,0,0.35), 0 0 0 1px rgba(212,168,120,0.18)',
                  textShadow: lightHeader
                    ? '0 1px 0 rgba(255,250,240,0.55)'
                    : '0 1px 2px rgba(0,0,0,0.45)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(212,168,120,0.92) 0%, rgba(184,137,90,0.96) 50%, rgba(139,94,47,0.98) 100%)';
                  e.currentTarget.style.borderColor = 'rgba(245,230,208,0.65)';
                  e.currentTarget.style.color = '#FFF8EC';
                  e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.32), 0 10px 28px -8px rgba(184,137,90,0.60)';
                  e.currentTarget.style.textShadow = 'none';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = lightHeader
                    ? 'linear-gradient(135deg, rgba(255,250,240,0.30) 0%, rgba(184,137,90,0.08) 100%)'
                    : 'linear-gradient(135deg, rgba(184,137,90,0.10) 0%, rgba(245,230,208,0.05) 100%)';
                  e.currentTarget.style.borderColor = 'rgba(184,137,90,0.70)';
                  e.currentTarget.style.color = accentColor;
                  e.currentTarget.style.boxShadow = lightHeader
                    ? 'inset 0 1px 0 rgba(255,250,240,0.45), 0 4px 16px -4px rgba(139,94,47,0.30)'
                    : 'inset 0 1px 0 rgba(255,250,240,0.12), 0 4px 18px -4px rgba(0,0,0,0.35), 0 0 0 1px rgba(212,168,120,0.18)';
                  e.currentTarget.style.textShadow = lightHeader
                    ? '0 1px 0 rgba(255,250,240,0.55)'
                    : '0 1px 2px rgba(0,0,0,0.45)';
                }}
                data-testid="pro-login-btn"
              >
                <span aria-hidden className="absolute inset-y-0 -left-[120%] w-1/2 group-hover:left-[140%] transition-[left] duration-1000 ease-out pointer-events-none" style={{ background: 'linear-gradient(120deg, transparent 0%, rgba(255,250,240,0.45) 50%, transparent 100%)', transform: 'skewX(-18deg)' }} />
                <User className="relative w-3.5 h-3.5" />
                <span className="relative">Espace Pro</span>
              </Link>
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden relative z-10 p-2"
              data-testid="mobile-menu-btn"
              style={{ color: accentColor }}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="lg:hidden fixed inset-0 top-20 bg-[#100B07] z-40"
            >
              <nav className="flex flex-col items-center justify-center h-full gap-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-xl uppercase tracking-[0.22em] font-medium"
                    style={{ color: location.pathname === link.path ? COPPER : 'rgba(245,230,208,0.72)' }}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/login"
                  className="mt-8 px-8 py-3 border uppercase tracking-[0.2em] text-sm transition-all duration-500"
                  style={{ borderColor: COPPER, color: COPPER }}
                  data-testid="mobile-pro-login-btn"
                >
                  Espace Professionnel
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-[#100B07] border-t border-white/5 relative overflow-hidden" data-testid="vitrine-footer">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -bottom-8 text-center select-none"
          style={{
            fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
            fontSize: 'clamp(8rem, 20vw, 22rem)',
            color: 'rgba(245,230,208,0.025)',
            letterSpacing: '0.05em',
            lineHeight: 1,
            fontWeight: 500,
          }}
        >
          GRANIPAL
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <img src={LOGO_URL} alt="Granipal" className="h-14 w-auto object-contain mb-6" />
              <p className="text-white/55 text-sm leading-relaxed max-w-md font-light">
                Marbrerie décorative haut de gamme, spécialisée dans le sur-mesure
                depuis plus de 25 ans. Cuisines, salles de bain, tables, escaliers,
                revêtements — du dessin à la pose.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.3em]" style={{ color: COPPER }}>
                <span className="w-8 h-px" style={{ background: COPPER, opacity: 0.6 }} />
                Sur-mesure · Île-de-France
              </div>
            </div>

            <div className="md:col-span-4">
              <h4 className="uppercase tracking-[0.25em] text-[10px] font-medium mb-6" style={{ color: COPPER }}>Atelier</h4>
              <ul className="space-y-4 text-white/60 text-sm font-light">
                <li className="flex items-start gap-3">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: `${COPPER}B0` }} />
                  <span>6 Rue de la porte écluse<br />91270 Vigneux-sur-Seine</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-3.5 h-3.5 shrink-0" style={{ color: `${COPPER}B0` }} />
                  <span>01 69 83 04 79 · 06 13 09 36 39</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-3.5 h-3.5 shrink-0" style={{ color: `${COPPER}B0` }} />
                  <a href="mailto:granipal@orange.fr" className="hover:text-[#F5E6D0] transition-colors">
                    granipal@orange.fr
                  </a>
                </li>
              </ul>
            </div>

            <div className="md:col-span-3">
              <h4 className="uppercase tracking-[0.25em] text-[10px] font-medium mb-6" style={{ color: COPPER }}>Navigation</h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <Link key={link.path} to={link.path} className="block text-white/55 text-sm font-light hover:text-[#F5E6D0] transition-colors">
                    {link.label}
                  </Link>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-white/5 mt-16 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/30 text-[11px] tracking-wide">
              © {new Date().getFullYear()} GRANIPAL — Marbrerie d'exception · Tous droits réservés
            </p>
            <div className="flex items-center gap-6 text-[11px] text-white/30 tracking-wide">
              <span>SIREN 413 277 096</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
